import { axiosSubscriptionInstance } from "../../../configs/AxiosInstance";
import { buildQuery } from "../../../service/comms/_query";
import { wrapCommsError } from "../../../service/comms/_error";

/**
 * Employee leave API — microservice-subscriptions `employee_leave_router.py`,
 * served from the same app as the booking routes, hence the same instance and
 * the same verb-first idiom as `service/bookingService.js`.
 */

/**
 * Leaves the caller may see, newest start first.
 *
 * Caller-scoped server side: their own leaves plus their direct reports'
 * (every leave for an executive). `employeeId` narrows to one person and is a
 * 403 when it names someone outside that set.
 *
 * @param {Function} get - The `get` verb from ApiContext.
 * @param {Object} params
 * @param {string} params.eventTypeId - Required by the API.
 * @param {string} [params.employeeId]
 * @param {string} [params.statusId] - A `leave_statuses` id.
 * @param {string} [params.startDate] - Keeps leaves ending after this.
 * @param {string} [params.endDate] - Keeps leaves starting before this.
 * @returns {Promise<Array<Object>>} Leave rows: `{ id, employee_id,
 *   employee_name, leave_type_id, leave_type, reason, start_date, end_date,
 *   status_id, status, status_changed_by_id, status_changed_at, created_at }`.
 */
export const fetchEmployeeLeaves = async (
  get,
  { eventTypeId, employeeId, statusId, startDate, endDate } = {}
) => {
  try {
    const query = buildQuery({
      event_type_id: eventTypeId,
      employee_id: employeeId,
      status_id: statusId,
      start_date: startDate,
      end_date: endDate,
    });
    // `get` resolves to the body, `{ message, data: { leaves, ... } }`.
    const body = await get(`/fetch-employee-leave${query}`, {
      instance: axiosSubscriptionInstance,
    });
    const leaves = body?.data?.leaves;
    return Array.isArray(leaves) ? leaves : [];
  } catch (err) {
    throw wrapCommsError(err, "Failed to load leaves");
  }
};

/**
 * Apply for, or change the status of, one or more leaves.
 *
 * - Apply: `{ operation: "ADD", employee_id, leave_type_id, start_date, end_date, reason? }`
 *   — dates are UTC instants, `end_date` exclusive. A new leave starts Pending.
 * - Approve / reject / withdraw: `{ operation: "ADD", id, leave_status_id }`.
 *
 * Approving cancels the coach's overlapping future bookings server side and
 * reports them in `cancelled_bookings`.
 *
 * @param {Function} post - The `post` verb from ApiContext.
 * @param {{ eventTypeId: string, leaves: Array<Object> }} params
 * @returns {Promise<{ leave_records?: Array<Object>, cancelled_bookings?: Array<Object> }>}
 */
export const modifyEmployeeLeaves = async (post, { eventTypeId, leaves } = {}) => {
  try {
    const response = await post(
      "/modify-employee-leave",
      { event_type_id: eventTypeId, leaves },
      { instance: axiosSubscriptionInstance }
    );
    // `post` resolves to { data, status } around { message, data }.
    return response?.data?.data || {};
  } catch (err) {
    throw wrapCommsError(err, "Failed to update leave");
  }
};
